import { useState } from "react";
import { formatDistanceToNowStrict } from "date-fns";
import { Loader2, Mail, X } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  FacebookIcon,
  InstagramIcon,
  LineIcon,
  LinkedInIcon,
  MessengerIcon,
  TelegramIcon,
  TikTokIcon,
  WhatsAppIcon,
  XIcon,
  YouTubeIcon,
} from "@/components/admin_dashboard/BrandIcons";
import {
  CONTACT_CHANNEL_TYPES,
  channelName,
  identifierLabel,
  type ContactIdentifier,
} from "@/lib/contactDisplay";

export type SocialPlatform = "instagram" | "tiktok" | "facebook" | "x" | "linkedin" | "youtube";

const SOCIAL_PLATFORMS: { key: SocialPlatform; label: string; Icon: typeof InstagramIcon }[] = [
  { key: "instagram", label: "Instagram", Icon: InstagramIcon },
  { key: "tiktok", label: "TikTok", Icon: TikTokIcon },
  { key: "facebook", label: "Facebook", Icon: FacebookIcon },
  { key: "x", label: "X", Icon: XIcon },
  { key: "linkedin", label: "LinkedIn", Icon: LinkedInIcon },
  { key: "youtube", label: "YouTube", Icon: YouTubeIcon },
];

const CHANNEL_ICONS: Record<string, typeof InstagramIcon> = {
  whatsapp_e164: WhatsAppIcon,
  instagram_psid: InstagramIcon,
  telegram_chat_id: TelegramIcon,
  line_user_id: LineIcon,
  facebook_psid: MessengerIcon,
};

const normalizedChannel = (value: string | null | undefined) => {
  switch (value?.toLowerCase()) {
    case "whatsapp_e164": return "whatsapp";
    case "instagram_psid": return "instagram";
    case "telegram_chat_id": return "telegram";
    case "line_user_id": return "line";
    case "facebook_psid":
    case "facebook": return "facebook_messenger";
    default: return value?.toLowerCase() ?? "";
  }
};

/** What to show for a channel whose id isn't human-readable. */
const unreadableFallback = (channelType: string) =>
  channelType === "instagram_psid" ? "Username not available yet" : "Connected";

const lastUsed = (value: string | null | undefined) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : `${formatDistanceToNowStrict(date)} ago`;
};

export function visibleChannels(identifiers: ContactIdentifier[]) {
  return identifiers
    .filter((identifier) => CONTACT_CHANNEL_TYPES.includes(identifier.channel_type))
    .sort((a, b) =>
      CONTACT_CHANNEL_TYPES.indexOf(a.channel_type) - CONTACT_CHANNEL_TYPES.indexOf(b.channel_type) ||
      new Date(b.last_seen_at ?? 0).getTime() - new Date(a.last_seen_at ?? 0).getTime()
    );
}

interface ContactChannelsSectionProps {
  contactId: string | null;
  identifiers: ContactIdentifier[];
  socialHandles: Partial<Record<SocialPlatform, string>>;
  preferredChannel: string | null;
  onSocialHandlesChange: (next: Partial<Record<SocialPlatform, string>>) => void;
}

/**
 * Every way this person can be reached (from their messages and imports),
 * plus social handles the owner adds by hand. Read-only channels, editable
 * handles; one quiet "Add a handle" action.
 */
export default function ContactChannelsSection({
  contactId,
  identifiers,
  socialHandles,
  preferredChannel,
  onSocialHandlesChange,
}: ContactChannelsSectionProps) {
  const [adding, setAdding] = useState(false);
  const [platform, setPlatform] = useState<SocialPlatform>("instagram");
  const [draft, setDraft] = useState("");
  const [savingPlatform, setSavingPlatform] = useState<SocialPlatform | null>(null);
  const channels = visibleChannels(identifiers);
  const handles = SOCIAL_PLATFORMS.filter((item) => socialHandles[item.key]);

  const saveHandle = async (target: SocialPlatform, value: string) => {
    if (!contactId) return;
    setSavingPlatform(target);
    const { data, error } = await (supabase as any).rpc("set_contact_social_handle", {
      p_contact_id: contactId,
      p_platform: target,
      p_handle: value,
    });
    setSavingPlatform(null);
    if (error) {
      toast.error(`Couldn't save that handle: ${error.message}`);
      return false;
    }
    onSocialHandlesChange((data ?? {}) as Partial<Record<SocialPlatform, string>>);
    return true;
  };

  const saveDraft = async () => {
    const handle = draft.trim().replace(/^@+/, "");
    if (!handle || savingPlatform !== null) return;
    if (await saveHandle(platform, handle)) {
      setDraft("");
      setAdding(false);
    }
  };

  return (
    <div className="space-y-2 border-t border-border pt-4">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-heading font-semibold text-foreground">Channels</h3>
        {contactId && !adding && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs font-normal text-muted-foreground"
            onClick={() => setAdding(true)}
          >
            Add a handle
          </Button>
        )}
      </div>

      {channels.length === 0 && handles.length === 0 && !adding && (
        <p className="text-sm text-muted-foreground">No messaging channels yet.</p>
      )}

      <div className="space-y-1">
        {channels.map((identifier) => {
          const Icon = CHANNEL_ICONS[identifier.channel_type];
          const label = identifierLabel(identifier) ?? unreadableFallback(identifier.channel_type);
          const used = lastUsed(identifier.last_seen_at);
          const isPreferred = normalizedChannel(identifier.channel_type) === normalizedChannel(preferredChannel);
          return (
            <div
              key={`${identifier.channel_type}:${identifier.identifier_value}`}
              className="grid grid-cols-[180px_minmax(0,1fr)] items-start gap-3 text-sm"
            >
              <span className="flex min-w-0 items-center gap-1.5 py-1 text-muted-foreground">
                {Icon ? <Icon size={14} className="shrink-0" /> : <Mail className="h-3.5 w-3.5 shrink-0" />}
                <span className="truncate">{channelName(identifier.channel_type)}</span>
                {isPreferred && (
                  <span className="shrink-0 rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-medium leading-none text-white">
                    Preferred
                  </span>
                )}
              </span>
              <span className="min-w-0 py-1">
                <span className="block truncate text-foreground" title={label}>{label}</span>
                {used && <span className="block text-xs text-muted-foreground">Last used {used}</span>}
              </span>
            </div>
          );
        })}

        {handles.map(({ key, label, Icon }) => (
          <div key={key} className="group grid grid-cols-[150px_minmax(0,1fr)] items-center gap-3 text-sm">
            <span className="flex min-w-0 items-center gap-1.5 text-muted-foreground">
              <Icon size={14} className="shrink-0" />
              <span className="truncate">{label}</span>
            </span>
            <span className="flex min-w-0 items-center gap-1 py-1 text-foreground">
              <span className="truncate">@{socialHandles[key]}</span>
              <button
                type="button"
                className="ml-auto rounded p-0.5 text-muted-foreground opacity-60 hover:bg-muted hover:opacity-100 focus-visible:opacity-100"
                aria-label={`Remove ${label} handle`}
                disabled={savingPlatform === key}
                onClick={() => void saveHandle(key, "")}
              >
                {savingPlatform === key ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5" />}
              </button>
            </span>
          </div>
        ))}
      </div>

      {adding && contactId && (
        <form
          className="grid grid-cols-[minmax(0,0.9fr)_minmax(0,1.45fr)] gap-2 pt-1"
          onSubmit={(event) => {
            event.preventDefault();
            void saveDraft();
          }}
        >
          <Select value={platform} onValueChange={(value) => setPlatform(value as SocialPlatform)}>
            <SelectTrigger className="h-8" aria-label="Platform">
              <SelectValue>
                {(() => {
                  const selected = SOCIAL_PLATFORMS.find((item) => item.key === platform)!;
                  return (
                    <span className="flex items-center gap-2">
                      <selected.Icon size={14} className="shrink-0" />
                      <span>{selected.label}</span>
                    </span>
                  );
                })()}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {SOCIAL_PLATFORMS.map((item) => {
                const Icon = item.Icon;
                return (
                  <SelectItem key={item.key} value={item.key}>
                    <span className="flex items-center gap-2">
                      <Icon size={14} className="shrink-0" />
                      <span>{item.label}</span>
                    </span>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
          <div className="relative">
            <span className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-sm text-muted-foreground">@</span>
            <Input
              autoFocus
              value={draft}
              onChange={(event) => setDraft(event.target.value.replace(/^@+/, ""))}
              onBlur={() => void saveDraft()}
              onKeyDown={(event) => {
                if (event.key === "Enter") event.currentTarget.blur();
                if (event.key === "Escape") {
                  setDraft("");
                  setAdding(false);
                }
              }}
              placeholder="handle"
              aria-label="Handle"
              className="h-8 min-w-[120px] pl-7 pr-9"
              disabled={savingPlatform !== null}
            />
            {savingPlatform !== null && (
              <Loader2 className="absolute right-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 animate-spin text-muted-foreground" />
            )}
          </div>
        </form>
      )}
    </div>
  );
}
